import Modal from "../../../components/ui/Modal";

interface TodoTag {
  id: string;
  name: string;
  color: string;
}

interface TodoCategory {
  id: string;
  name: string;
  color: string;
  isTask?: boolean;
}

interface TodoItemFormProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  categories: TodoCategory[];
  availableTags: TodoTag[];
  text: string;
  setText: (text: string) => void;
  priority: "low" | "medium" | "high" | "critical";
  setPriority: (priority: "low" | "medium" | "high" | "critical") => void;
  selectedTags: TodoTag[];
  setSelectedTags: (tags: TodoTag[]) => void;
  estimatedTime: number;
  setEstimatedTime: (time: number) => void;
  estimatedUnit: "minutes" | "hours" | "days";
  setEstimatedUnit: (unit: "minutes" | "hours" | "days") => void;
  selectedCategoryId: string;
  setSelectedCategoryId: (id: string) => void;
  onSubmit: () => void;
  submitText: string;
  isEdit?: boolean;
}

export default function TodoItemForm({
  isOpen,
  onClose,
  title,
  categories,
  availableTags,
  text,
  setText,
  priority,
  setPriority,
  selectedTags,
  setSelectedTags,
  estimatedTime,
  setEstimatedTime,
  estimatedUnit,
  setEstimatedUnit,
  selectedCategoryId,
  setSelectedCategoryId,
  onSubmit,
  submitText,
  isEdit = false,
}: TodoItemFormProps) {
  const toggleTag = (tag: TodoTag) => {
    if (selectedTags.find((t) => t.id === tag.id)) {
      setSelectedTags(selectedTags.filter((t) => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="space-y-4">
        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kategori *</label>
            <select
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="">Kategori seçin...</option>
              {categories
                .filter((cat) => !cat.isTask)
                .map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
            </select>
            {categories.every((cat) => cat.isTask) && (
              <p className="text-xs text-red-600 mt-1">Tüm kategoriler görev sistemi kategorileri. Lütfen normal bir kategori oluşturun.</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Görev Açıklaması *</label>
          <input
            type="text"
            maxLength={255}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Görev açıklaması girin..."
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">{text.length}/255 karakter</p>
            {text.length > 200 && (
              <p className="text-xs text-orange-600">
                {text.length > 240 ? "Kalan karakter az!" : "Kalan karakter: " + (255 - text.length)}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Öncelik</label>
          <div className="grid grid-cols-2 sm:flex gap-2">
            <button
              onClick={() => setPriority("low")}
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                priority === "low" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Düşük
            </button>
            <button
              onClick={() => setPriority("medium")}
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                priority === "medium" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Orta
            </button>
            <button
              onClick={() => setPriority("high")}
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                priority === "high" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Yüksek
            </button>
            <button
              onClick={() => setPriority("critical")}
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                priority === "critical" ? "bg-red-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Kritik
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tahmini Süre</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              max={estimatedUnit === "minutes" ? 60 : estimatedUnit === "hours" ? 24 : 30}
              value={estimatedTime}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                const maxValue = estimatedUnit === "minutes" ? 60 : estimatedUnit === "hours" ? 24 : 30;
                setEstimatedTime(Math.min(value, maxValue));
              }}
              className="w-20 sm:w-24 px-2 sm:px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
            />
            <select
              value={estimatedUnit}
              onChange={(e) => {
                const newUnit = e.target.value as "minutes" | "hours" | "days";
                setEstimatedUnit(newUnit);
                const maxValue = newUnit === "minutes" ? 60 : newUnit === "hours" ? 24 : 30;
                if (estimatedTime > maxValue) {
                  setEstimatedTime(maxValue);
                }
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="minutes">Dakika</option>
              <option value="hours">Saat</option>
              <option value="days">Gün</option>
            </select>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {estimatedUnit === "minutes" && "Maksimum 60 dakika"}
            {estimatedUnit === "hours" && "Maksimum 24 saat"}
            {estimatedUnit === "days" && "Maksimum 30 gün"}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Etiketler</label>
          {availableTags.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
              Henüz etiket yok. Etiketler sekmesinden etiket ekleyebilirsiniz.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    selectedTags.find((t) => t.id === tag.id) ? "text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  style={selectedTags.find((t) => t.id === tag.id) ? { backgroundColor: tag.color } : {}}>
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors">
            İptal
          </button>
          <button
            onClick={onSubmit}
            disabled={!text.trim() || (!isEdit && !selectedCategoryId)}
            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {submitText}
          </button>
        </div>
      </div>
    </Modal>
  );
}
